/**
 * Application Constants
 * 
 * Type-safe toegang tot environment variables en app-wide constants.
 * Gebruik altijd deze constants in plaats van directe toegang tot import.meta.env.
 * 
 * Regels (zie 03_SECURITY_DEPLOYMENT.md):
 * - Geen hardcoded URLs of andere config
 * - Alle environment variables moeten via VITE_ prefix
 * - Sensitive data hoort niet in frontend code
 */

// ============================================
// Environment Variables (Type-safe)
// ============================================

import type { RolType } from '@types';
import pkg from '../../package.json';

export const CONFIG = {
  // API Configuration
  API_URL: '/api',
  
  // App Configuration
  APP_NAME: import.meta.env.VITE_APP_NAME as string || 'Delphi',
  APP_VERSION: pkg.version as string || '0.0.0',
  
  // Logging
  LOG_LEVEL: import.meta.env.VITE_LOG_LEVEL as 'debug' | 'info' | 'warn' | 'error' || 'info',
  
  // Environment
  NODE_ENV: import.meta.env.MODE as 'development' | 'production' | 'test' || 'development',
  IS_PROD: import.meta.env.PROD as boolean || false,
  IS_DEV: import.meta.env.DEV as boolean || true,
  
  // Feature Flags (toekomstig)
  FEATURE_OFFLINE_MODE: import.meta.env.VITE_FEATURE_OFFLINE_MODE === 'true' || false,
  FEATURE_DARK_MODE: import.meta.env.VITE_FEATURE_DARK_MODE === 'true' || false,
} as const;

// ============================================
// Application Constants
// ============================================

// Authentication
export const AUTH = {
  CSRF_COOKIE_NAME: 'XSRF-TOKEN',
  LOGIN_PATH: '/login',
  DASHBOARD_PATH: '/dashboard', // Projectie scherm voor deelnemers
  SESSIES_PATH: '/sessies',    // Beheer omgeving voor facilitators
  SESSION_TIMEOUT_MINUTES: 60, // 1 uur
} as const;

// API Endpoints (relatief, worden gecombineerd met CONFIG.API_URL)
// Domein termen zijn gebaseerd op backend model namen (lees: /backend/src/main/kotlin/org/plusmin/delphi/model/)
export const ENDPOINTS = {
  // Health & Status (technisch, blijft Engels)
  HEALTH: '/actuator/health',
  
  // Authenticatie (gebruikers)
  INLOGGEN: '/gebruikers/login',
  UITLOGGEN: '/gebruikers/logout',
  
  // Organisaties
  ORGANISATIES: '/organisaties',
  ORGANISATIE: (id: number) => `/organisaties/${id}`,
  
  // Gebruikers
  GEBRUIKERS: '/gebruikers',
  GEBRUIKER: (id: number) => `/gebruikers/${id}`,
  
  // Rollen
  ROLLEN: '/rollen',
  ROL: (id: number) => `/rollen/${id}`,
  
  // Assen (Axis blijft Engels - gereserveerd woord in backend)
  AXES: '/assen',
  AXIS: (id: number) => `/assen/${id}`,
  
  // Stellingen
  STELLINGEN: '/stellingen',
  STELLING: (id: number) => `/stellingen/${id}`,
  
  // Sjablonen
  SJABLONEN: '/sjablonen',
  SJABLOON: (id: number) => `/sjablonen/${id}`,
  
  // Sessies
  SESSIES: '/sessies',
  SESSIE: (sessieCode: string) => `/sessies/${sessieCode}`,
  SESSIE_CODE: (code: string) => `/sessies/${code}`,
  SESSIE_BEGELEIDER: (begeleiderId: number) => `/sessies/begeleider/${begeleiderId}`,
  SESSIE_STATUSBEWERKEN: (sessieCode: string) => `/sessies/${sessieCode}/status`,
  SESSIE_ASSEN_STATUSBEWERKEN: (sessieCode: string, axisId: number) =>
    `/sessies/${sessieCode}/assen/${axisId}/status`,
  
  // Deelnemers
  DEELNEMERS: '/deelnemers',
  DEELNEMER: (id: number) => `/deelnemers/${id}`,
  DEELNEMERS_SESSIE: (sessieCode: string) => `/deelnemers/sessie/${sessieCode}`,
  DEELNEMER_TOKEN: (token: string) => `/deelnemers/token/${token}`,
  
  // Antwoorden
  ANTWOORDEN: '/antwoorden',
  ANTWOORD: (id: number) => `/antwoorden/${id}`,
  ANTWOORDEN_DEELNEMER: (token: string) => `/antwoorden/deelnemer/${token}`,
  ANTWOORDEN_SESSIE: (sessieCode: string) => `/antwoorden/sessie/${sessieCode}`,
  ANTWOORDEN_STELLING: (stellingId: number) => `/antwoorden/stelling/${stellingId}`,
  
  // Export
  EXPORTS: '/exports',
  EXPORT: (id: number) => `/exports/${id}`,
  EXPORT_SESSIE: (sessieCode: string) => `/exports/sessie/${sessieCode}`,
  
  // Import
  IMPORT_ORGANISATIE: '/import/organisatie',
} as const;

// ============================================
// UI Constants
// ============================================

// RolType volgorde (matcht backend enum: HULPVRAGER, VRIJWILLIGER, COORDINATOR, BESTUURDER)
export const ROL_TYPE_ORDER = ['HULPVRAGER', 'VRIJWILLIGER', 'COORDINATOR', 'BESTUURDER'] as const;

// Kleurmap voor rollen en geaggregeerd in spinnenwebdiagrammen
// Elke rol-type, "Geaggregeerd" en vergelijkingsmodi hebben een vaste kleur
export const ROL_COLOR_MAP: Record<string, { backgroundColor: string; borderColor: string }> = {
  HULPVRAGER: { backgroundColor: 'rgba(59, 130, 246, 0.2)', borderColor: 'rgba(59, 130, 246, 1)' },
  VRIJWILLIGER: { backgroundColor: 'rgba(34, 197, 94, 0.2)', borderColor: 'rgba(34, 197, 94, 1)' },
  COORDINATOR: { backgroundColor: 'rgba(168, 85, 247, 0.2)', borderColor: 'rgba(168, 85, 247, 1)' },
  BESTUURDER: { backgroundColor: 'rgba(249, 115, 22, 0.2)', borderColor: 'rgba(249, 115, 22, 1)' },
  Geaggregeerd: { backgroundColor: 'rgba(239, 68, 68, 0.2)', borderColor: 'rgba(239, 68, 68, 1)' },
  AndereSessie: { backgroundColor: 'rgba(236, 72, 153, 0.2)', borderColor: 'rgba(236, 72, 153, 1)' },
} as const;

// Sorteer rolTypes volgens vaste volgorde, houdt alleen de types die aanwezig zijn
export const sortRoleTypes = (roleTypes: string[]): string[] => {
  return [...roleTypes].sort((a, b) => {
    const indexA = ROL_TYPE_ORDER.indexOf(a as RolType);
    const indexB = ROL_TYPE_ORDER.indexOf(b as RolType);
    return indexA - indexB;
  });
};

export const UI = {
  // Layout
  MAX_CONTENT_WIDTH: '1440px',
  SIDEBAR_WIDTH: '256px',
  HEADER_HEIGHT: '64px',
  
  // Pagination
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  
  // Animations
  TRANSITION_DURATION: '200ms',
  ANIMATION_DURATION: '300ms',
  
  // Z-index layers
  Z_INDEX: {
    DROPDOWN: 100,
    MODAL_BACKDROP: 200,
    MODAL: 300,
    TOOLTIP: 400,
    TOAST: 500,
  },
  
  // Toast notifications
  TOAST: {
    DURATION: {
      SUCCESS: 4000,
      ERROR: 6000,
      WARNING: 5000,
      INFO: 3000,
    },
  },
} as const;

// ============================================
// Validation Constants
// ============================================

export const VALIDATION = {
  // Common
  MIN_LENGTH: {
    NAME: 2,
    DESCRIPTION: 10,
    PASSWORD: 8,
  },
  MAX_LENGTH: {
    NAME: 100,
    DESCRIPTION: 500,
    TEXT: 1000,
    TEXTAREA: 5000,
  },
  
  // Specific
  SESSION_CODE_LENGTH: 6,
  PIN_CODE_LENGTH: 6,
  PIN_CODE_PATTERN: /^\d{6}$/, // Exact 6 digits
  
  // Scores
  MIN_SCORE: 1,
  MAX_SCORE: 5,
  
  // Files
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: ['image/jpeg', 'image/png', 'application/pdf'],
} as const;

// ============================================
// Storage Keys (voor niet-sensitive data)
// ============================================

export const STORAGE = {
  // LocalStorage keys
  THEME_PREFERENCE: 'delphi-theme-preference',
  LAATSTE_BEZOCHTE_SESSIE: 'delphi-last-visited-session',
  
  // SessionStorage keys
  SESSIE_TOKEN: 'delphi-session-token', // Voor participant tokens (niet httpOnly)
  FORM_DRAFT: (formId: string) => `delphi-form-draft-${formId}`,
} as const;

// ============================================
// Time Constants
// ============================================

export const TIME = {
  // Debounce delays
  DEBOUNCE: {
    SEARCH: 300,
    INPUT: 500,
    AUTO_SAVE: 1000,
  },
  
  // Timeouts
  API_TIMEOUT: 10000, // 10 seconden
  CONNECTION_RETRY_DELAY: 5000, // 5 seconden
  
  // Polling intervals
  SESSION_STATS_POLL_INTERVAL: 5000, // 5 seconden
  PARTICIPANT_PROGRESS_POLL_INTERVAL: 3000, // 3 seconden
} as const;
